import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Prompt, GeoMetadata } from "@/lib/types";
import { getEmbeddedPublicProfile } from "@/utils/public-profiles";
import { toast } from "sonner";

const PAGE_SIZE = 10;

export const useUserPrompts = (profileUserId: string, currentUserId?: string) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const fetchPrompts = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (!profileUserId) return;
    
    if (reset) {
      setLoading(true);
      setPage(0);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // Pagination
      const from = pageNum * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data: promptsData, error: promptsError } = await supabase
        .from('prompts')
        .select(`
          *,
          public_profiles!prompts_user_id_fkey(username, avatar_id)
        `)
        .eq('user_id', profileUserId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (promptsError) throw promptsError;

      if (!promptsData || promptsData.length === 0) {
        setHasMore(false);
        if (reset) setPrompts([]);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      // Check if there are more pages
      if (promptsData.length < PAGE_SIZE) {
        setHasMore(false);
      }

      const promptIds = promptsData.map(p => p.id);

      // Batch fetch counts
      const [commentCounts, likeCounts, userLikes, userSaves] = await Promise.all([
        supabase.from('prompt_comments').select('prompt_id').in('prompt_id', promptIds),
        supabase.from('prompt_likes').select('prompt_id').in('prompt_id', promptIds),
        currentUserId ? supabase.from('prompt_likes').select('prompt_id').in('prompt_id', promptIds).eq('user_id', currentUserId) : Promise.resolve({ data: [] }),
        currentUserId ? supabase.from('saved_prompts').select('prompt_id').in('prompt_id', promptIds).eq('user_id', currentUserId) : Promise.resolve({ data: [] })
      ]);

      // Create lookup maps
      const commentCountMap = new Map<string, number>();
      const likeCountMap = new Map<string, number>();
      const userLikedSet = new Set(userLikes.data?.map(l => l.prompt_id) || []);
      const userSavedSet = new Set(userSaves.data?.map(s => s.prompt_id) || []);

      commentCounts.data?.forEach(c => {
        commentCountMap.set(c.prompt_id, (commentCountMap.get(c.prompt_id) || 0) + 1);
      });
      likeCounts.data?.forEach(l => {
        likeCountMap.set(l.prompt_id, (likeCountMap.get(l.prompt_id) || 0) + 1);
      });

      // Map prompts with details - DEFENSIVE: Normalize all fields
      const newPrompts = promptsData.map((prompt: any) => {
        const profile = getEmbeddedPublicProfile(prompt.public_profiles);
        const geoMetadata = prompt.geo_metadata as GeoMetadata | null;

        return {
          id: prompt.id,
          title: prompt.title || (prompt.content ? prompt.content.substring(0, 50) : 'Untitled Prompt'),
          content: prompt.content || '',
          description: prompt.description || undefined,
          // DEFENSIVE: Ensure category is never null/undefined
          category: (prompt.category || 'general-chat') as Exclude<import("@/lib/types").Category, 'all'>,
          categoryEditCount: prompt.category_edit_count || 0,
          userId: prompt.user_id,
          username: profile?.username || 'Anonymous',
          avatarId: profile?.avatar_id || 1,
          createdAt: prompt.created_at || new Date().toISOString(),
          likes: likeCountMap.get(prompt.id) || 0,
          comments: commentCountMap.get(prompt.id) || 0,
          isLiked: userLikedSet.has(prompt.id),
          isSaved: userSavedSet.has(prompt.id),
          isPinned: false,
          isRemix: prompt.is_remix || false,
          remixOf: prompt.remix_of,
          geoMetadata: geoMetadata || undefined,
        };
      });

      setPrompts(prev => reset ? newPrompts : [...prev, ...newPrompts]);
    } catch (error: any) {
      console.error('Error fetching user prompts:', error);
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [profileUserId, currentUserId]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPrompts(nextPage, false);
    }
  }, [page, loadingMore, hasMore, fetchPrompts]);

  const refresh = useCallback(() => {
    setPage(0);
    fetchPrompts(0, true);
  }, [fetchPrompts]);

  useEffect(() => {
    refresh();
  }, [profileUserId, currentUserId]);

  const updatePrompt = (id: string, updatedPrompt: Partial<Prompt>) => {
    setPrompts(prevPrompts => 
      prevPrompts.map(prompt => 
        prompt.id === id ? { ...prompt, ...updatedPrompt } : prompt
      )
    );
    toast.success("Prompt updated");
  };

  const deletePrompt = (id: string) => {
    setPrompts(prevPrompts => prevPrompts.filter(prompt => prompt.id !== id));
    toast.success("Prompt deleted");
  };

  return {
    prompts,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh,
    updatePrompt,
    deletePrompt,
    totalCount: prompts.length,
  };
};
